import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["consultantsContainer", "contractorsContainer", "otherVendorsContainer", "stakeholdersContainer", "flexContainer", "flexBlankTemplate", "submitBtn", "errors", "resolveSection", "resolveOptions", "resolveToggleBtn", "resolveBtn",
                    "roleGroup", "groupTemplate", "newRolePicker", "newRoleSelect", "newRoleCustom", "newRoleButton",
                    "ownerRepsContainer"]
  static values = { updateUrl: String, ownerCompanyId: Number }

  connect() {
    // Track roles the user explicitly cleared via the team-card "Remove company"
    // confirmation modal. This list is sent with the save payload so the backend
    // can distinguish a deliberate clear from an accidental empty submission.
    this._clearedRoles = new Set()
    this._onExplicitRemove = (event) => {
      if (event.detail?.role) this._clearedRoles.add(event.detail.role)
    }
    this.element.addEventListener("team-card:explicit-remove", this._onExplicitRemove)
  }

  disconnect() {
    if (this._onExplicitRemove) {
      this.element.removeEventListener("team-card:explicit-remove", this._onExplicitRemove)
    }
  }

  addCard(event) {
    const role = event.currentTarget.dataset.role
    const container = this._containerFor(role)
    if (!container) return

    // Builder "Companies" list: clone the dedicated blank template so a new
    // card starts on the "Company type..." prompt instead of inheriting the
    // first card's type.
    if (role === "flex_companies" && this.hasFlexBlankTemplateTarget) {
      const blank = this.flexBlankTemplateTarget.content.firstElementChild
      if (blank) container.appendChild(blank.cloneNode(true))
      return
    }

    const firstCard = container.querySelector("[data-controller*='team-card']")
    if (firstCard) {
      const clone = firstCard.cloneNode(true)
      clone.querySelectorAll("input").forEach(input => {
        // Checkboxes (e.g. the "External" toggle) keep their submit value —
        // only their checked state resets.
        if (input.type === "checkbox") { input.checked = false } else { input.value = "" }
      })
      clone.querySelectorAll("select").forEach(select => {
        // Type dropdowns in the Consultant/Subcontractor sections reset to
        // their section's own type, not the first option.
        const def = select.dataset.defaultValue
        if (def) { select.value = def } else { select.selectedIndex = 0 }
      })
      clone.querySelectorAll("[data-team-card-target='memberChips']").forEach(el => { el.innerHTML = "" })
      // Reset the flex "Custom..." label input back to hidden (its select
      // was just reset to the placeholder/default option).
      clone.querySelectorAll("[data-flex-role-custom]").forEach(el => el.classList.add("hidden"))
      delete clone.dataset.teamCardInitialCompanyIdValue
      delete clone.dataset.teamCardInitialCompanyNameValue
      delete clone.dataset.teamCardInitialMembersValue
      const addBtn = clone.querySelector("[data-team-card-target='addMembersBtn']")
      if (addBtn) { addBtn.classList.add("hidden"); addBtn.classList.remove("inline-flex") }
      container.appendChild(clone)
    }
  }

  // ── Role-grouped builder layout (Phelipe 2026-07-22) ──────────────────
  // Groups carry the role; the bottom Add Company asks for the role FIRST
  // and opens a new group. Cards keep their (hidden) type controls preset
  // to the group role, so save-time bucketing works unchanged.

  addCardToGroup(event) {
    const group = event.currentTarget.closest("[data-edit-dev-team-target='roleGroup']")
    if (!group) return
    this._appendBlankCard(group, group.dataset.roleName)
  }

  toggleNewRolePicker() {
    if (!this.hasNewRolePickerTarget) return
    const nowHidden = this.newRolePickerTarget.classList.toggle("hidden")
    if (!nowHidden) {
      this.newRoleSelectTarget.value = ""
      this.newRoleCustomTarget.value = ""
      this.newRoleCustomTarget.classList.add("hidden")
      this.newRoleSelectTarget.focus()
    }
  }

  stopPropagation(event) { event.stopPropagation() }

  newRoleSelectChanged() {
    const custom = this.newRoleSelectTarget.value === "__custom__"
    this.newRoleCustomTarget.classList.toggle("hidden", !custom)
    if (custom) this.newRoleCustomTarget.focus()
  }

  confirmNewRole() {
    const sel = this.newRoleSelectTarget.value
    const roleName = sel === "__custom__" ? this.newRoleCustomTarget.value.trim() : sel
    if (!roleName) {
      this._showErrors(["Pick a company type first (or type a custom one)."])
      return
    }
    this._hideErrors()

    // Same role already has a block? Just add another company into it.
    let group = this.roleGroupTargets.find(g => (g.dataset.roleName || "").toLowerCase() === roleName.toLowerCase())
    if (!group) {
      group = this.groupTemplateTarget.content.firstElementChild.cloneNode(true)
      group.dataset.roleName = roleName
      const label = group.querySelector("[data-group-label]")
      if (label) label.textContent = roleName
      this.flexContainerTarget.appendChild(group)
    }
    this._appendBlankCard(group, roleName)
    this.toggleNewRolePicker()
  }

  // Clone the blank card into a group with its type controls preset to the
  // group's role and kept hidden (the group header shows the role).
  _appendBlankCard(group, roleName) {
    if (!this.hasFlexBlankTemplateTarget) return
    const blank = this.flexBlankTemplateTarget.content.firstElementChild
    if (!blank) return
    const card = blank.cloneNode(true)

    const select = card.querySelector("[data-flex-role-select]")
    const custom = card.querySelector("[data-flex-role-custom]")
    if (select) {
      const hasOption = Array.from(select.options).some(o => o.value === roleName)
      select.value = hasOption ? roleName : "__custom__"
      select.classList.add("hidden")
    }
    if (custom) {
      custom.value = (select && select.value === "__custom__") ? roleName : ""
      custom.classList.add("hidden")
    }

    const container = group.querySelector("[data-group-cards]")
    if (container) container.appendChild(card)
    card.querySelector("[data-team-card-target='companyInput']")?.focus()
  }

  async save() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    this._hideErrors()
    this._clearUnresolvedHighlights()
    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Saving..."

    try {
      // Block save when a card has a typed company name but no selection from the
      // dropdown. The autocomplete clears the hidden id on every keystroke, so any
      // edit that doesn't end in a selection would otherwise silently drop the card
      // and the backend would lose all of its members.
      const unresolved = this._findUnresolvedCompanies()
      if (unresolved.length > 0) {
        unresolved.forEach(u => u.card.classList.add("ring-2", "ring-red-400", "rounded-lg"))
        const names = unresolved.map(u => `"${u.text}"`).join(", ")
        this._showErrors([
          `Please select a company from the dropdown for ${names}, or remove the entry before saving.`
        ])
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Save Changes"
        return
      }

      const isBuilder = this.hasFlexContainerTarget

      // Contractors section is hidden on bidding projects (added via Manage Bidding
      // Contractors instead), so the target may be absent — guard like the others.
      let contractors = this.hasContractorsContainerTarget ? this._collectCards(this.contractorsContainerTarget) : []
      let stakeholders = this.hasStakeholdersContainerTarget ? this._collectCards(this.stakeholdersContainerTarget) : []
      const ownerReps = this._collectOwnerReps()
      const ownerMembers = this._collectOwnerMembers()

      let consultants, otherVendors, flexCompanies
      if (isBuilder) {
        // Builder: all non-Owner companies live in ONE stacked list and every
        // card carries its own type dropdown — the dropdown value (not a
        // section) decides the payload bucket, so re-classifying is just
        // changing the select.
        const buckets = this._collectBuilderBuckets()
        consultants = buckets.consultants
        otherVendors = buckets.otherVendors
        flexCompanies = buckets.flexCompanies

        const missingType = buckets.invalid
        if (missingType) {
          this._showErrors(["Please pick a company type (or type a custom one) for every company."])
          this.submitBtnTarget.disabled = false
          this.submitBtnTarget.textContent = "Save Changes"
          return
        }
      } else {
        consultants = this._collectCards(this.consultantsContainerTarget)
        otherVendors = this.hasOtherVendorsContainerTarget ? this._collectCards(this.otherVendorsContainerTarget) : []
        flexCompanies = []
      }

      // A company also put under Owner's Reps switches to Owner's Rep: it
      // leaves its other section and its people come along (Phelipe 2026-10-21).
      const movedRoles = new Set()
      const pullIntoReps = (list, roleKey) => list.filter(card => {
        const rep = ownerReps.find(r => String(r.company_id) === String(card.company_id))
        if (!rep) return true
        rep.members = [...new Set([...(rep.members || []), ...(card.members || [])])]
        movedRoles.add(roleKey)
        return false
      })
      consultants = pullIntoReps(consultants, "consultants")
      contractors = pullIntoReps(contractors, "contractors")
      otherVendors = pullIntoReps(otherVendors, "other_vendors")
      stakeholders = pullIntoReps(stakeholders, "stakeholders")
      flexCompanies = pullIntoReps(flexCompanies, "flex_companies")

      const duplicateError = this._validateDuplicateCompanies(consultants, contractors, otherVendors, stakeholders, flexCompanies, ownerReps)
      if (duplicateError) {
        this._showErrors([duplicateError])
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Save Changes"
        return
      }

      const clearedRoles = new Set([...(this._clearedRoles || []), ...movedRoles])
      if (isBuilder) {
        // The unified list is authoritative for all three payload buckets —
        // mark them managed so a company whose type changed (its old bucket
        // now empty) is actually removed from the old role instead of
        // tripping the empty-payload guard.
        clearedRoles.add("consultants")
        clearedRoles.add("other_vendors")
        clearedRoles.add("flex_companies")
      }
      const payload = {
        consultants,
        contractors,
        other_vendors: otherVendors,
        stakeholders,
        owner_reps: ownerReps,
        owner_members: ownerMembers,
        cleared_roles: Array.from(clearedRoles)
      }
      // Only send flex_companies when the section exists (builder projects) —
      // its absence means "no change", mirroring the other sections.
      if (isBuilder) payload.flex_companies = flexCompanies

      if (this._ownerRepMoneyConfirmed) payload.confirm_owner_rep_money = true
      const response = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (response.ok && data.success) {
        window.location.reload()
      } else if ((data.needs_owner_rep_confirm || []).length) {
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Save Changes"
        this._askOwnerRepMoney(data.needs_owner_rep_confirm)
      } else {
        this._showErrors(data.errors || ["Update failed"])
        this.submitBtnTarget.disabled = false
        this.submitBtnTarget.textContent = "Save Changes"
      }
    } catch {
      this._showErrors(["A network error occurred. Please try again."])
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = "Save Changes"
    }
  }

  // Moving a company with pay requests under Owner's Reps (Phelipe
  // 2026-10-21): say so first. Confirm saves again with the OK; Cancel
  // leaves the team as it was so the pay requests can be sorted out first.
  _askOwnerRepMoney(list) {
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const wrap = document.createElement("div")
    wrap.className = "fixed inset-0 z-[2147483006] flex items-center justify-center bg-black/50"
    wrap.innerHTML = `
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" data-panel>
        <div class="px-6 pt-5">
          <h3 class="text-base font-semibold text-gray-900 mb-1">Pay requests on this project</h3>
          <p class="text-sm text-gray-600">${list.map(m => `<b>${esc(m.company)}</b> has pay requests or an approved budget here as <b>${esc(m.role)}</b>.`).join("<br>")}
            If you go ahead it becomes the Owner's Rep and keeps its ${esc(list[0].role)} spot only for Finances: its pay requests turn into Past PRs (already paid, no signatures) that the owner side can delete or add to.</p>
          <p class="text-[13px] text-gray-500 mt-2">Cancel to leave the team as it is — you can fix those pay requests by hand and try again.</p>
        </div>
        <div class="flex items-center justify-end gap-3 px-6 py-4 mt-3 border-t border-gray-200">
          <button type="button" data-cancel class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">Cancel</button>
          <button type="button" data-ok class="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand-dark cursor-pointer">Make it Owner's Rep</button>
        </div>
      </div>`
    const close = () => wrap.remove()
    wrap.addEventListener("click", close)
    wrap.querySelector("[data-panel]").addEventListener("click", e => e.stopPropagation())
    wrap.querySelector("[data-cancel]").addEventListener("click", close)
    wrap.querySelector("[data-ok]").addEventListener("click", () => {
      close()
      this._ownerRepMoneyConfirmed = true
      this.save()
    })
    document.body.appendChild(wrap)
  }

  toggleResolve() {
    if (!this.hasResolveOptionsTarget) return
    const isHidden = this.resolveOptionsTarget.classList.toggle("hidden")
    if (this.hasResolveToggleBtnTarget) {
      this.resolveToggleBtnTarget.textContent = isHidden ? "Resolve Now" : "Cancel"
    }
  }

  async resolveContractors() {
    const btn = this.resolveBtnTarget
    const url = btn.dataset.resolveUrl
    const selected = this.resolveOptionsTarget.querySelector("input[name='resolve_winner']:checked")
    const winningCompanyId = selected ? selected.value : "0"
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    btn.disabled = true
    btn.textContent = "Resolving..."

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({ winning_company_id: winningCompanyId })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        window.location.reload()
      } else {
        this._showErrors(data.errors || ["Failed to resolve bidding contractors."])
        btn.disabled = false
        btn.textContent = "Confirm"
      }
    } catch {
      this._showErrors(["A network error occurred. Please try again."])
      btn.disabled = false
      btn.textContent = "Confirm"
    }
  }

  _containerFor(role) {
    switch (role) {
      case "consultants": return this.hasConsultantsContainerTarget ? this.consultantsContainerTarget : null
      case "contractors": return this.hasContractorsContainerTarget ? this.contractorsContainerTarget : null
      case "other_vendors": return this.hasOtherVendorsContainerTarget ? this.otherVendorsContainerTarget : null
      case "stakeholders": return this.hasStakeholdersContainerTarget ? this.stakeholdersContainerTarget : null
      case "owner_reps": return this.hasOwnerRepsContainerTarget ? this.ownerRepsContainerTarget : null
      case "flex_companies": return this.hasFlexContainerTarget ? this.flexContainerTarget : null
      default: return null
    }
  }

  _showErrors(messages) {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.innerHTML = messages.map(m => `<p>${m}</p>`).join("")
    this.errorsTarget.classList.remove("hidden")
    // The banner lives in the footer next to Save, so it's already in view for the
    // normal save flow. block:"nearest" guarantees visibility for the bidding-
    // contractor resolve flow (whose Confirm button sits higher up) while doing
    // nothing — no jarring jump — when the banner is already on screen.
    this.errorsTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  _hideErrors() {
    if (!this.hasErrorsTarget) return
    this.errorsTarget.classList.add("hidden")
    this.errorsTarget.innerHTML = ""
  }

  _findUnresolvedCompanies() {
    const containers = [
      this.hasConsultantsContainerTarget  ? this.consultantsContainerTarget  : null,
      this.hasContractorsContainerTarget  ? this.contractorsContainerTarget  : null,
      this.hasOtherVendorsContainerTarget ? this.otherVendorsContainerTarget : null,
      this.hasStakeholdersContainerTarget ? this.stakeholdersContainerTarget : null,
      this.hasOwnerRepsContainerTarget    ? this.ownerRepsContainerTarget    : null,
      this.hasFlexContainerTarget         ? this.flexContainerTarget         : null
    ].filter(Boolean)

    const unresolved = []
    containers.forEach(container => {
      container.querySelectorAll("[data-controller*='team-card']").forEach(card => {
        const input  = card.querySelector("[data-team-card-target='companyInput']")
        const hidden = card.querySelector("[data-team-card-target='companyHidden']")
        const inputVal  = (input?.value  || "").trim()
        const hiddenVal = (hidden?.value || "").trim()
        if (inputVal && !hiddenVal) {
          unresolved.push({ card, text: inputVal })
        }
      })
    })
    return unresolved
  }

  _clearUnresolvedHighlights() {
    this.element.querySelectorAll("[data-controller*='team-card']").forEach(card => {
      card.classList.remove("ring-2", "ring-red-400", "rounded-lg")
    })
  }

  // Owner's Reps (James / Phelipe 2026-10-20): cards like the Stakeholders,
  // with a dropdown of the companies from Settings → General → Owner Reps.
  _collectOwnerReps() {
    return this.hasOwnerRepsContainerTarget ? this._collectCards(this.ownerRepsContainerTarget) : []
  }

  _validateDuplicateCompanies(consultants, contractors, otherVendors, stakeholders, flexCompanies = [], ownerReps = []) {
    const roleGroups = [
      { name: "Consultants", cards: consultants },
      { name: "Contractors", cards: contractors },
      { name: "Other Vendors", cards: otherVendors },
      { name: "Stakeholders", cards: stakeholders },
      { name: "Owner's Reps", cards: ownerReps },
      { name: "Other Companies", cards: flexCompanies }
    ]

    const seen = new Map()
    if (this.ownerCompanyIdValue) {
      seen.set(String(this.ownerCompanyIdValue), "Owner")
    }

    for (const group of roleGroups) {
      for (const card of group.cards) {
        const companyId = String(card.company_id)
        if (seen.has(companyId)) {
          const companyName = this._findCompanyName(companyId) || `Company #${companyId}`
          return `"${companyName}" is already assigned as ${seen.get(companyId)}. Each company can only have one role per project.`
        }
        seen.set(companyId, group.name)
      }
    }

    return null
  }

  _findCompanyName(companyId) {
    const cards = this.element.querySelectorAll("[data-controller*='team-card']")
    for (const card of cards) {
      const hidden = card.querySelector("[data-team-card-target='companyHidden']")
      if (hidden?.value === companyId) {
        const input = card.querySelector("[data-team-card-target='companyInput']")
        if (input?.tagName === "SELECT") {
          return input.options[input.selectedIndex]?.text || null
        }
        return input?.value || null
      }
    }
    return null
  }

  _collectOwnerMembers() {
    const ownerEl = this.element.querySelector("[data-controller*='owner-members']")
    if (!ownerEl) return []
    const chips = ownerEl.querySelectorAll("[data-chip-email]")
    return Array.from(chips).map(c => c.dataset.chipEmail).filter(Boolean)
  }

  _collectCards(container) {
    if (!container) return []
    const cards = container.querySelectorAll("[data-controller*='team-card']")
    const result = []

    cards.forEach(card => {
      const companyHidden = card.querySelector("[data-team-card-target='companyHidden']")
      const companyId = companyHidden?.value
      if (!companyId) return

      const chips = card.querySelectorAll("[data-team-card-target='memberChips'] [data-chip-email]")
      const members = Array.from(chips).map(chip => chip.dataset.chipEmail).filter(Boolean)

      const entry = { company_id: companyId, members }
      // Builder cards may carry an "External — no sign-up" toggle (item 8).
      const externalCb = card.querySelector("[data-external-checkbox]")
      if (externalCb) entry.external = externalCb.checked

      result.push(entry)
    })

    return result
  }

  // Builder projects: every non-Owner company sits in the ONE unified
  // "Companies" list and carries an inline type dropdown. Bucket each card by
  // that dropdown: "Consultant"/"Subcontractor" go to their fixed payload
  // keys; everything else (including Custom labels) is a flex_companies
  // entry. `invalid` flags a card whose type is missing.
  _collectBuilderBuckets() {
    const buckets = { consultants: [], otherVendors: [], flexCompanies: [], invalid: false }
    if (!this.hasFlexContainerTarget) return buckets

    this.flexContainerTarget.querySelectorAll("[data-controller*='team-card']").forEach(card => {
      const companyId = card.querySelector("[data-team-card-target='companyHidden']")?.value
      if (!companyId) return

      const chips = card.querySelectorAll("[data-team-card-target='memberChips'] [data-chip-email]")
      const members = Array.from(chips).map(chip => chip.dataset.chipEmail).filter(Boolean)
      const externalCb = card.querySelector("[data-external-checkbox]")
      const external = externalCb ? externalCb.checked : false

      const inlineSelect = card.querySelector("[data-flex-role-select]")
      const type = inlineSelect ? inlineSelect.value : ""
      const customLabel = (card.querySelector("[data-flex-role-custom]")?.value || "").trim()

      if (type === "Consultant") {
        buckets.consultants.push({ company_id: companyId, members, external })
      } else if (type === "Subcontractor") {
        buckets.otherVendors.push({ company_id: companyId, members, external })
      } else {
        if (!type || (type === "__custom__" && !customLabel)) buckets.invalid = true
        buckets.flexCompanies.push({
          company_id: companyId,
          members,
          role_name: type,
          custom_label: customLabel,
          external
        })
      }
    })

    return buckets
  }
}
